import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Newspaper, Share2 } from "lucide-react";
import api from "@/lib/api";
import { shareArticle } from "@/lib/share";

const EASE = [0.22, 1, 0.36, 1];

function ShareBtn({ article, testid, light }) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        shareArticle({ title: article.title, text: article.body.slice(0, 120), path: `/news/${article.id}`, ogPath: `/api/og/news/${article.id}` });
      }}
      data-testid={testid}
      aria-label={`Share ${article.title}`}
      title="Share this story"
      className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-colors duration-200 cursor-pointer ${
        light ? "bg-white text-league-blue hover:bg-league-blue hover:text-white" : "bg-league-blue text-white hover:bg-league-red"
      }`}
    >
      <Share2 size={15} />
    </button>
  );
}

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = news.find((n) => n.featured) || news[0];
  const rest = news.filter((n) => n.id !== featured?.id);

  return (
    <main className="pt-28 pb-24 bg-league-mist min-h-screen" data-testid="news-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
          <Newspaper size={14} /> League Wire
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-league-ink tracking-tighter mt-3 mb-14">
          NEWS & <span className="text-outline">UPDATES</span>
        </h1>

        {loading && <p className="text-muted-foreground" data-testid="news-loading">Loading stories…</p>}

        {featured && (
          <motion.article
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: EASE }}
            className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 bg-white rounded-2xl border border-black/5 overflow-hidden shadow-[0_20px_50px_-20px_rgba(0,74,173,0.2)]"
            data-testid={`news-featured-${featured.id}`}
          >
            <div className="absolute top-4 right-4 z-10">
              <ShareBtn article={featured} testid={`news-share-${featured.id}`} light />
            </div>
            <Link to={`/news/${featured.id}`} className="contents" data-testid={`news-featured-link-${featured.id}`}>
              <img src={featured.image_url} alt={featured.title} className="w-full h-72 lg:h-full object-cover" />
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 rounded-full bg-league-blue text-white text-[11px] font-bold uppercase tracking-widest">{featured.tag}</span>
                  <span className="text-xs text-muted-foreground font-semibold">{format(new Date(featured.published_at), "dd MMM yyyy")}</span>
                </div>
                <h2 className="font-oswald text-2xl sm:text-4xl font-semibold text-league-ink mt-4 leading-tight">{featured.title}</h2>
                <p className="text-muted-foreground mt-4 leading-relaxed">{featured.body}</p>
                {featured.image_url_2 && (
                  <img src={featured.image_url_2} alt={`${featured.title} — more`} className="mt-6 w-full max-w-xs rounded-xl border border-black/10 object-cover" data-testid="news-featured-image-2" />
                )}
              </div>
            </Link>
          </motion.article>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((n, i) => (
            <motion.article
              key={n.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: EASE }}
              className="relative bg-white rounded-2xl border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,74,173,0.25)] transition-[transform,box-shadow] duration-300"
              data-testid={`news-article-${n.id}`}
            >
              <div className="absolute top-3 right-3 z-10">
                <ShareBtn article={n} testid={`news-share-${n.id}`} light />
              </div>
              <Link to={`/news/${n.id}`} className="block" data-testid={`news-card-link-${n.id}`}>
                {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-48 object-cover" />}
                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full bg-league-blue/10 text-league-blue text-[11px] font-bold uppercase tracking-widest">{n.tag}</span>
                    <span className="text-xs text-muted-foreground font-semibold">{format(new Date(n.published_at), "dd MMM yyyy")}</span>
                  </div>
                  <h3 className="font-oswald text-lg font-semibold text-league-ink mt-3 leading-snug">{n.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">{n.body}</p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </main>
  );
}
